import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Text, MessageBar, MessageBarType } from 'office-ui-fabric-react';
import { PrimaryButton, DefaultButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import clsx from 'clsx';
import StackGrid from '../../components/StackGrid';
import StatementRejectionDialog from '../../components/StatementRejectionDialog';
import StatementRegenerateDialog from '../../components/StatementRegenerateDialog';
import StatementReleaseDialog from '../../components/StatementReleaseDialog';
import { useStatementRejection } from '../../context/statement-rejection';
import DocumentUpload from '../DocumentUpload';
import TextButton from '../TextButton';
import {
  statementApproval,
  uploadStatementAttachment,
  fetchStatementDetails,
  statementRegeneration,
} from '../../actions/statements';
import { showFlashMessage } from '../../actions/ui';
import styles from './StatementDetailsActionRequired.style';
import { can } from '../../utils/auth';

const StatementDetailsActionRequired = ({
  id,
  statementEndDate,
  isLastStep,
  isStepBeforeBAM,
  className: classNameProp,
  attachments,
  attachmentFileType,
  ...other
}) => {
  const className = clsx(styles.root, classNameProp);

  const history = useHistory();
  const dispatch = useDispatch();
  const { partnerAddress } = useParams();
  const uploadref = useRef(null);

  const [isCancelRequested, setIsCancelRequested] = useState(true);
  const [isCancelRegenerated, setIsCancelRegenerated] = useState(true);
  const [isReleaseRequested, setIsReleaseRequested] = useState(isLastStep);
  const [isErrorApproving, setIsErrorApproving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(
    'Error found. Please try again later or contact support for assistance',
  );
  const { message } = useStatementRejection();

  let primaryButtonText;

  const successMessage = status => {
    if (status === 'Approved' && can('approveStatement')) return 'Statement approved';
    if (status === 'Approved' && !can('approveStatement'))
      return 'Statement submitted for approval';
    if (status === 'Approved' && can('approveStatement') && isLastStep)
      return 'Statement approved and released to partner';
    if (status === 'Rejected' && can('rejectStatement'))
      return 'Statement rejected and sent to the RA';
    if (status === 'Generated' && can('generateStatement'))
      return 'Statement regeneration initiated';
    return '';
  };

  const failureMessage = status => {
    if (status === 'Approved') {
      setErrorMsg(
        'Error Approving Statement. Please try again later or contact support for assistance',
      );
    } else if (status === 'Rejected') {
      setErrorMsg(
        'Error Rejecting Statement. Please try again later or contact support for assistance',
      );
    }
    return 'Error found. Please try again later or contact support for assistance';
  };

  const attachmentTextMessage = () => {
    if (attachmentFileType === 'evidence') {
      return 'Attach Reconciliation Evidence';
    }
    if (attachmentFileType === 'executiveApproval') {
      return 'Attach Executive Approval';
    }
    return 'Attach File';
  };

  const handleUpload = files => {
    if (files.length) {
      dispatch(
        uploadStatementAttachment(id, partnerAddress, { files, type: attachmentFileType }),
      ).then(result => {
        if (result && !result.error) {
          dispatch(showFlashMessage(result.payload.message, { type: 'success' }));
          dispatch(fetchStatementDetails(partnerAddress, id));
        }
      });
    }
  };

  const handleSubmit = async status => {
    if (status === 'Approved' && attachmentFileType === 'executiveApproval') {
      const hasBusinessApproval = attachments.find(
        file => file.documentType === attachmentFileType,
      );
      if (!hasBusinessApproval) {
        setErrorMsg('Please upload required executive approval as attachment and try again');
        setIsErrorApproving(true);
        setIsReleaseRequested(false);
        return;
      }
    }
    const formValues = {
      status,
    };
    if (status === 'Rejected') formValues.remarks = message;

    const response = await dispatch(statementApproval(id, formValues, successMessage(status)));

    if (!response.error) {
      history.push(`/statements/`);
      setIsErrorApproving(false);
    } else {
      failureMessage(status);
      setIsErrorApproving(true);
    }
    // closes the dialog boxes
    setIsCancelRequested(true);
    setIsReleaseRequested(false);
  };

  const handleRegeneration = async status => {
    const response = await dispatch(
      statementRegeneration(id, { remarks: message }, successMessage(status)),
    );

    if (!response.error) {
      history.push(`/statements/`);
      setIsErrorApproving(false);
    } else {
      failureMessage();
      setIsErrorApproving(true);
    }
    // closes the dialog boxes
    setIsCancelRegenerated(true);
  };

  const actionText = can('approveStatement')
    ? 'Review the statement, complete all necessary actions, and approve.'
    : 'Review the statement, attach reconciliation evidence as necessary, and submit for approval.';

  switch (true) {
    case can('approveStatement') && isStepBeforeBAM:
      primaryButtonText = 'Approve and release statement to BAM';
      break;
    case !can('approveStatement') && isStepBeforeBAM:
      primaryButtonText = 'Release statement to BAM';
      break;
    case can('approveStatement') && !isLastStep:
      primaryButtonText = 'Approve statement';
      break;
    case can('approveStatement') && isLastStep:
      primaryButtonText = 'Approve and release statement';
      break;
    default:
      primaryButtonText = 'Submit for Approval';
      break;
  }

  return (
    <div className={className} {...other}>
      {isErrorApproving && (
        <MessageBar
          messageBarType={MessageBarType.error}
          messageBarIconProps={{ iconName: 'Error' }}
          dismissButtonAriaLabel="Close"
          nowrap
        >
          {errorMsg}
        </MessageBar>
      )}
      <MessageBar
        messageBarType={MessageBarType.info}
        messageBarIconProps={{ iconName: 'Info' }}
        isMultiline
        dismissButtonAriaLabel="Close"
        nowrap
      >
        <Text>Action Required</Text>
        <br />
        <StackGrid smCols={1} mdCols={1} lgCols={1} gap={0}>
          <Text className={styles.actionsText}>{actionText}</Text>
          {can('uploadStatementAttachment') && attachmentFileType && (
            <div className={styles.attachment}>
              <IconButton
                iconProps={{ iconName: 'Attach' }}
                title="Attach"
                ariaLabel="Attach"
                onClick={() => uploadref.current.click()}
              />
              <TextButton type="button" noPadding onClick={() => uploadref.current.click()}>
                {attachmentTextMessage()}
              </TextButton>
              <DocumentUpload
                className={styles.uploader}
                ref={uploadref}
                onUpload={handleUpload}
                accept=".*"
                id="btn_statementAttachmentUpload"
              />
            </div>
          )}
          <div className={styles.buttonDiv}>
            {can('generateStatement') && (
              <>
                <DefaultButton type="submit" onClick={() => setIsCancelRegenerated(false)}>
                  Regenerate Statement
                </DefaultButton>
                <StatementRegenerateDialog
                  onDismiss={() => setIsCancelRegenerated(true)}
                  onSubmit={() => handleRegeneration('Generated')}
                  hidden={isCancelRegenerated}
                />
              </>
            )}
            {can('rejectStatement') && (
              <>
                <DefaultButton type="submit" onClick={() => setIsCancelRequested(false)}>
                  Reject Statement
                </DefaultButton>
                <StatementRejectionDialog
                  onDismiss={() => setIsCancelRequested(true)}
                  onSubmit={() => handleSubmit('Rejected')}
                  hidden={isCancelRequested}
                />
              </>
            )}

            <PrimaryButton
              type="submit"
              className={styles.primaryButton}
              onClick={
                () =>
                  can('approveStatement') || isStepBeforeBAM
                    ? setIsReleaseRequested(true)
                    : handleSubmit('Approved') // No popup dialogue for RA 'Submit for approval'
              }
            >
              {primaryButtonText}
            </PrimaryButton>
            {(can('approveStatement') || isStepBeforeBAM) && isReleaseRequested && (
              <StatementReleaseDialog
                onDismiss={() => setIsReleaseRequested(false)}
                onSubmit={() => handleSubmit('Approved')}
                action={primaryButtonText}
                hidden={!isReleaseRequested}
                isStepBeforeBAM={isStepBeforeBAM}
              />
            )}
          </div>
        </StackGrid>
      </MessageBar>
    </div>
  );
};

StatementDetailsActionRequired.propTypes = {
  statementEndDate: PropTypes.string,
  id: PropTypes.string,
  isLastStep: PropTypes.bool,
  isStepBeforeBAM: PropTypes.bool,
  className: PropTypes.string,
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      documentName: PropTypes.string,
      documentType: PropTypes.string,
    }),
  ),
  attachmentFileType: PropTypes.string,
};

StatementDetailsActionRequired.defaultProps = {
  statementEndDate: '',
  id: '',
  isLastStep: false,
  isStepBeforeBAM: false,
  className: '',
  attachments: [],
  attachmentFileType: '',
};

export default StatementDetailsActionRequired;
